import './action-close-dialog';
import './action-edit-recipe';
import './action-log-delete';
import './action-log-download';
import './action-reload';

// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';

// Import style for web component
import styles from './web-component-style.scss?inline';

// eslint-disable-next-line require-jsdoc
@customElement( 'uap-log-header' )
export class AutomatorLogHeader extends LitElement {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	// Properties
	// The title of the recipe
	@property( { type: String, attribute: 'recipe-title' } ) accessor title;

	// An array with a summary of the recipe
	// This includes the start date, end date, user data, and so on
	@property( { type: Array, attribute: false } ) accessor summary = [];

	// The URL to edit the recipe
	@property( { type: String, attribute: 'recipe-edit-url' } ) accessor recipeEditURL;

	// The URL to delete the log
	@property( { type: String, attribute: 'log-delete-url' } ) accessor logDeleteURL;

	// The recipe status ID (5 = in progress/scheduled)
	@property( { type: String, attribute: 'recipe-status-id' } ) accessor recipeStatusID;

	// This property is used to indicate to the web component that it shouldn't try to retrieve data from the server
	// and that it should use the data provided when it was created
	@property( { type: Boolean, attribute: 'import-mode' } ) accessor isImportMode = false;

	// Check if the sidebar is open
	@property( { type: Boolean, attribute: 'sidebar-open' } ) accessor isSidebarOpen = false;

	// The object that will be sent to the user for download
	@property( { type: Object, attribute: false } ) accessor downloadObject;

	// eslint-disable-next-line require-jsdoc
	render() {
		return html`
			<div class="log-header">    
				${ this._templateActions }
				${ this._templateTitle }
				${ this._templateSummary }
			</div>
		`;
	}

	/**
	 * Returns a template that contains the buttons that perform a variety of actions
	 *
	 * @return {TemplateResult} The header actions
	 */
	get _templateActions() {
		return html`
			<div class="log-header-actions">
				<div class="log-header-actions-left">
					${ this._templateActionsLog }
					${ this._templateActionsRecipe }
				</div>
				
				<div class="log-header-actions-right">
					${ this._templateActionsCloseDialog }
				</div>
			</div>
		`;
	}

	/**
	 * Returns the group of actions that contain the return button
	 *
	 * @return {TemplateResult|undefined} The button to close the dialog
	 */
	get _templateActionsCloseDialog() {
		// Check if it's the offline mode
		// If so, we can't reload, download or delete the log
		if ( this.isImportMode ) {
			return;
		}

		// If the sidebar is open, don't show the close button. We'll show it on the sidebar
		if ( this.isSidebarOpen ) {
			return;
		}

		return html`
			<div class="log-header-actions-group">
				<uap-log-header-action-close-dialog></uap-log-header-action-close-dialog>
			</div>
		`;
	}

	/**
	 * Returns the action that use the LOG data
	 *
	 * @return {TemplateResult|undefined} Buttons to perform quick actions
	 */
	get _templateActionsLog() {
		// Check if it's the offline mode
		// If so, we can't reload, download or delete the log
		if ( this.isImportMode ) {
			return;
		}

		return html`
			<div class="log-header-actions-group">
				<uap-log-header-action-reload></uap-log-header-action-reload>
				
				<uap-log-header-action-download-log 
					recipe-title=${ this.title }
					.downloadObject=${ this.downloadObject }
				></uap-log-header-action-download-log>

				<uap-log-header-action-delete-log log-delete-url=${ this.logDeleteURL } recipe-status-id=${ this.recipeStatusID }></uap-log-header-action-delete-log>
			</div>
		`;
	}

	/**
	 * Returns the group of actions that perform tasks to the recipe that created the log
	 *
	 * @return {TemplateResult} The buttons to edit the recipe linked to the log
	 */
	get _templateActionsRecipe() {
		return html`
			<div class="log-header-actions-group">
				<uap-log-header-action-edit-recipe recipe-edit-url=${ this.recipeEditURL }></uap-log-header-action-edit-recipe>
			</div>
		`;
	}

	/**
	 * Returns the template that contains the title and run number
	 *
	 * @return {TemplateResult} The title
	 */
	get _templateTitle() {
		return html`
			<h1 class="log-header-title">${ this.title }</h1>
		`;
	}

	/**
	 * Returns the table that contains a summary of the recipe
	 *
	 * @return {TemplateResult} The summary of the recipe run
	 */
	get _templateSummary() {
		return html`
			<table class="log-header-summary">
				<tbody>
					${
						repeat(
							this.summary,
							( row, index ) => index,
							row => html`
								<tr>
									<td class="log-header-summary__key">
										${ row.key }
									</td>
									<td class="log-header-summary__value">
										${ row.value }
									</td>
								</tr>
							`
						)
					}
				</tbody>
			</table>
		`;
	}
}